import Team from "@assets/icons/new/team.svg?react";
import { AutocompleteOptionType } from "@components/common/members-autocomplete/members-autocomplete-types";
import { SphereAvatar } from "@components/header/sphere-avatar";
import { blue, Checkmark2Icon, neutral, NO_TRANSLATE_CLASS } from "@flat-ui";
import { Box, Typography } from "@mui/material";
import { EDecimalToHex } from "@styles/common-colors";
import { withEllipsis } from "@styles/common-styles";
import { addTransparency } from "@utils/ui-utils";
import AutosuggestHighlightMatch from "autosuggest-highlight/match";
import AutosuggestHighlightParse from "autosuggest-highlight/parse";
import { useMemo } from "react";

export interface UserItem {
  /**
   * The title for the first row of the user item, ideally shows the
   * user's full name or the email if there is no name.
   */
  title: string;

  /**
   * Optionally shows a row below the title, ideally it shows the
   * user's email address, if the name is shown in the title
   */
  subtitle?: string;

  /**
   * Optional user initials to be shown in the avatar.
   */
  userInitials?: string;

  /**
   * Optional url for an image to be shown in the avatar.
   */
  userAvatarImg?: string;

  /** The type that will represent the member */
  type?: AutocompleteOptionType | string;
}

interface Props {
  /** Optionally you can directly pass the item to be rendered. */
  item: UserItem;

  /**
   * Optional disabled message to show in the item.
   * This property only shows the message itself but does not make the item disabled,
   * that should be handled in parent component, most likely a "li" element.
   */
  disabledMessage?: string | null;

  /** Input value typed on the input field */
  inputValue?: string;

  /**
   * Determines whether the item is active (selected).
   * When set to true, an indicator (such as an icon) will be displayed
   * to indicate that the item is currently selected or active.
   */
  isActive?: boolean;
}

/** Width for the user avatar */
const AVATAR_WIDTH = "50px";

/** Class name to highlight part of string */
const HIGHLIGHT_CLASS = "text_highlight";

export function UserListItem({
  item,
  disabledMessage,
  inputValue,
  isActive,
}: Props): JSX.Element {
  const userItem = {
    ...item,
    type: item.type || "member",
  };

  const highlightedTitle = useMemo(() => {
    /** Early return if there is no input value entered */
    if (!inputValue) {
      return userItem.title;
    }

    // Finding the match parts
    const matches = AutosuggestHighlightMatch(userItem.title, inputValue);
    const parts = AutosuggestHighlightParse(userItem.title, matches);

    return (
      <span>
        {parts.map((part, index) => (
          <span className={part.highlight ? HIGHLIGHT_CLASS : ""} key={index}>
            {part.text}
          </span>
        ))}
      </span>
    );
  }, [inputValue, userItem.title]);

  const highlightedSubTitle = useMemo(() => {
    /** Early return if there is no input value entered */
    if (!inputValue || !userItem.subtitle) {
      return userItem.subtitle;
    }

    // Finding the match parts
    const matches = AutosuggestHighlightMatch(userItem.subtitle, inputValue);
    const parts = AutosuggestHighlightParse(userItem.subtitle, matches);

    return (
      <span>
        {parts.map((part, index) => (
          <span className={part.highlight ? HIGHLIGHT_CLASS : ""} key={index}>
            {part.text}
          </span>
        ))}
      </span>
    );
  }, [userItem.subtitle, inputValue]);

  return (
    <Box
      component="div"
      display="flex"
      alignItems="center"
      padding="5px 5px"
      width="100%"
      gap={1}
      sx={{
        background: isActive
          ? addTransparency({
              color: neutral[500],
              alpha: EDecimalToHex.thirtyEight,
            })
          : "",
        "&:hover": {
          background: neutral[100],
          cursor: isActive ? "initial" : "pointer",
        },
      }}
    >
      <Box
        component="div"
        sx={{
          display: "flex",
          alignItems: "center",
          width: "100%",
        }}
      >
        <Box component="div" sx={{ width: AVATAR_WIDTH }}>
          {userItem.type === "team" ? (
            <SphereAvatar
              icon={<Team />}
              size="small"
              backgroundColor={neutral[700]}
            />
          ) : (
            <SphereAvatar
              size={"small"}
              initials={userItem.userInitials ?? ""}
              src={userItem.userAvatarImg}
            />
          )}
        </Box>
        <Box
          component="div"
          sx={{
            // If there is a disabled message we have two columns, otherwise we have one.
            width: disabledMessage
              ? `calc(50% - ${AVATAR_WIDTH} / 2)`
              : `calc(100% - ${AVATAR_WIDTH})`,
          }}
        >
          <Box
            className={NO_TRANSLATE_CLASS}
            component="div"
            sx={{
              width: "100%",
              color: isActive ? blue[500] : neutral[800],
              fontSize: "14px",
              display: "flex",
              alignItems: "center",
              [`& .${HIGHLIGHT_CLASS}`]: {
                fontWeight: "bold",
              },
            }}
          >
            <Typography
              component={"var"}
              sx={{
                flex: 1,
                fontSize: "14px",
                lineHeight: "20px",
                fontWeight: 600,
                ...withEllipsis,
              }}
            >
              {highlightedTitle}
            </Typography>

            {isActive && <Checkmark2Icon />}
          </Box>

          {userItem.subtitle && (
            <Box
              className={NO_TRANSLATE_CLASS}
              component="div"
              sx={{
                ...withEllipsis,
                width: "100%",
                color: neutral[500],
                fontSize: "12px",
                [`& .${HIGHLIGHT_CLASS}`]: {
                  fontWeight: "bold",
                },
              }}
            >
              {highlightedSubTitle}
            </Box>
          )}
        </Box>
        {disabledMessage && (
          <Box
            component="div"
            sx={{
              width: `calc(50% - ${AVATAR_WIDTH} / 2)`,
              color: neutral[500],
              fontSize: "12px",
              paddingLeft: "20px",
              textAlign: "end",
            }}
          >
            {disabledMessage}
          </Box>
        )}
      </Box>
    </Box>
  );
}
